import { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, ShieldCheck, PenLine, PlusCircle, Trash2, Check } from "lucide-react";
import client from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";

/**
 * Admin console for the "Suggested reading" catalogue.
 *
 * Only content_admin and platform_admin roles are allowed by the
 * backend. The screen renders a friendly refusal for anyone else.
 *
 * The list groups entries by product (Start / Grow) and shows,
 * per row, the anchor, title, author, why, sign-off state, and a
 * quick set of actions: edit, sign, retire.
 */
export default function AdminBooksScreen() {
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [productFilter, setProductFilter] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const qs = productFilter ? `?product=${productFilter}` : "";
      const r = await client.get(`/content-admin/books${qs}`);
      setEntries(r.data.entries || []);
    } catch (e) {
      setErr(e?.response?.status === 403
        ? "You need a content administrator role to open this console."
        : (e?.response?.data?.detail || "Could not load the book catalogue."));
      setEntries([]);
    } finally {
      setBusy(false);
    }
  }, [productFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { start: [], grow: [] };
    for (const e of entries) {
      if (e.product === "grow") g.grow.push(e);
      else g.start.push(e);
    }
    return g;
  }, [entries]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-books-screen">
      <header className="mb-4">
        <div className="k-label mb-1 flex items-center gap-2">
          <BookOpen size={14} /> books catalogue
        </div>
        <h1 className="klareet-serif text-3xl mb-2" style={{ color: "hsl(var(--klareet-ink))" }}>
          Suggested reading, on the panel
        </h1>
        <p className="k-quiet max-w-3xl">
          Add, edit, and sign off entries. Only signed AND active entries feed the Report screen.
          Editing an entry drops its sign-off, so a reviewer must sign it again.
        </p>
      </header>

      <div className="mb-6 flex items-end gap-3 flex-wrap">
        <label className="flex flex-col" style={{ minWidth: "10rem" }}>
          <span className="k-label mb-1">product filter</span>
          <select
            className="k-input"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            data-testid="admin-books-filter"
          >
            <option value="">All products</option>
            <option value="start">Start</option>
            <option value="grow">Grow</option>
          </select>
        </label>
        <NewEntryButton onCreated={load} />
      </div>

      {err && (
        <div className="k-card mb-4" style={{ borderColor: "hsl(0 60% 55%)" }} data-testid="admin-books-error">
          <div className="k-label mb-1" style={{ color: "hsl(0 60% 45%)" }}>error</div>
          <div style={{ color: "hsl(var(--klareet-ink))" }}>{err}</div>
        </div>
      )}

      {["start", "grow"].map((prod) => grouped[prod].length > 0 && (
        <section key={prod} className="mb-8" data-testid={`admin-books-${prod}-section`}>
          <h2 className="klareet-serif text-2xl mb-3" style={{ color: "hsl(var(--klareet-ink))" }}>
            {prod === "grow" ? "Grow catalogue" : "Start catalogue"}
            <span className="k-quiet !text-sm ml-2">{grouped[prod].length} entries</span>
          </h2>
          <div className="space-y-3">
            {grouped[prod].map((e) => (
              <BookRow key={e.id} entry={e} onChanged={load} />
            ))}
          </div>
        </section>
      ))}

      {!busy && entries.length === 0 && !err && (
        <div className="k-card" data-testid="admin-books-empty">
          <div className="k-quiet">No entries yet. Add the first one above.</div>
        </div>
      )}
    </div>
  );
}


function BookRow({ entry, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmRetire, setConfirmRetire] = useState(false);

  const retire = async () => {
    setBusy(true); setErr("");
    try {
      await client.post(`/content-admin/books/${entry.id}/retire`);
      setConfirmRetire(false);
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <div
      className="k-card"
      data-testid={`admin-book-row-${entry.id}`}
      style={{
        borderColor: entry.panel_signed && entry.active
          ? "hsl(var(--klareet-line))"
          : "hsl(45 90% 55% / 0.5)",
      }}
    >
      {!editing && !signing && (
        <>
          <div className="flex items-start gap-3 justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-2 mb-1">
                <span className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }} data-testid={`admin-book-title-${entry.id}`}>
                  {entry.title}
                </span>
                <span className="k-quiet !text-xs">by {entry.author}</span>
                <span
                  className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full"
                  style={{
                    color: "hsl(var(--klareet-muted))",
                    background: "hsl(var(--klareet-canvas-2))",
                    border: "1px solid hsl(var(--klareet-line-soft))",
                  }}
                  data-testid={`admin-book-anchor-${entry.id}`}
                >
                  {entry.anchor.replaceAll("_", " ")}
                </span>
                <StatusPill entry={entry} />
              </div>
              <div className="k-quiet !text-sm">{entry.why}</div>
              {entry.panel_signed && entry.panel_reviewer_id && (
                <div className="k-quiet !text-xs mt-2">
                  signed by {entry.panel_reviewer_id}
                  {entry.panel_signoff_date && ` on ${new Date(entry.panel_signoff_date).toLocaleDateString()}`}
                </div>
              )}
              {err && <div className="k-quiet !text-xs mt-2" style={{ color: "hsl(0 60% 45%)" }}>{err}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className="k-btn-outline !text-xs"
                onClick={() => setEditing(true)}
                data-testid={`admin-book-edit-${entry.id}`}
                disabled={busy}
              >
                <PenLine size={12} /> Edit
              </button>
              {!entry.panel_signed && entry.active && (
                <button
                  type="button"
                  className="k-btn-outline !text-xs"
                  onClick={() => setSigning(true)}
                  data-testid={`admin-book-sign-${entry.id}`}
                  disabled={busy}
                >
                  <Check size={12} /> Sign
                </button>
              )}
              {entry.active && (
                <button
                  type="button"
                  className="k-btn-outline !text-xs"
                  onClick={() => setConfirmRetire(true)}
                  data-testid={`admin-book-retire-${entry.id}`}
                  disabled={busy}
                  style={{ color: "hsl(0 60% 45%)" }}
                >
                  <Trash2 size={12} /> Retire
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {editing && (
        <BookForm
          initial={entry}
          onCancel={() => setEditing(false)}
          onSubmit={async (form) => {
            try {
              await client.put(`/content-admin/books/${entry.id}`, form);
              setEditing(false);
              onChanged?.();
            } catch (e) {
              return e?.response?.data?.detail || "Could not save.";
            }
          }}
        />
      )}

      {signing && (
        <SignForm
          onCancel={() => setSigning(false)}
          onSubmit={async (payload) => {
            try {
              await client.post(`/content-admin/books/${entry.id}/panel-review`, payload);
              setSigning(false);
              onChanged?.();
            } catch (e) {
              return e?.response?.data?.detail || "Could not sign.";
            }
          }}
        />
      )}

      <ConfirmDialog
        open={confirmRetire}
        onCancel={() => setConfirmRetire(false)}
        onConfirm={retire}
        title="Retire this book?"
        description={<>Retiring <span style={{ fontWeight: 600, color: "hsl(var(--klareet-ink))" }}>{entry.title}</span> stops it feeding the Report right away.</>}
        consequences={[
          "It stops appearing in anyone's suggested reading immediately",
          "Its panel sign-off no longer applies",
          "You would need to add it again to bring it back",
        ]}
        confirmWord="RETIRE"
        confirmLabel="Retire book"
        tone="danger"
        testId="retire-book-confirm"
      />
    </div>
  );
}


function StatusPill({ entry }) {
  if (!entry.active) {
    return (
      <span
        className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full"
        style={{
          color: "hsl(0 55% 40%)",
          background: "hsl(0 55% 95%)",
          border: "1px solid hsl(0 55% 80%)",
        }}
        data-testid={`admin-book-status-retired-${entry.id}`}
      >
        retired
      </span>
    );
  }
  if (!entry.panel_signed) {
    return (
      <span
        className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full"
        style={{
          color: "hsl(30 70% 35%)",
          background: "hsl(45 90% 95%)",
          border: "1px solid hsl(45 90% 75%)",
        }}
        data-testid={`admin-book-status-unsigned-${entry.id}`}
      >
        unsigned
      </span>
    );
  }
  return (
    <span
      className="klareet-mono !text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1"
      style={{
        color: "hsl(140 40% 30%)",
        background: "hsl(140 40% 95%)",
        border: "1px solid hsl(140 40% 75%)",
      }}
      data-testid={`admin-book-status-signed-${entry.id}`}
    >
      <ShieldCheck size={10} /> signed &amp; live
    </span>
  );
}


function NewEntryButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        className="k-btn"
        onClick={() => setOpen(true)}
        data-testid="admin-books-new-btn"
      >
        <PlusCircle size={14} /> Add entry
      </button>
    );
  }
  return (
    <div className="k-card w-full" data-testid="admin-books-new-form-wrap">
      <BookForm
        initial={{ product: "start", anchor: "", title: "", author: "", why: "", order: 0, active: true }}
        onCancel={() => setOpen(false)}
        onSubmit={async (form) => {
          try {
            await client.post("/content-admin/books", form);
            setOpen(false);
            onCreated?.();
          } catch (e) {
            return e?.response?.data?.detail || "Could not save.";
          }
        }}
      />
    </div>
  );
}


function BookForm({ initial, onSubmit, onCancel }) {
  const [product, setProduct] = useState(initial.product || "start");
  const [anchor, setAnchor] = useState(initial.anchor || "");
  const [title, setTitle] = useState(initial.title || "");
  const [author, setAuthor] = useState(initial.author || "");
  const [why, setWhy] = useState(initial.why || "");
  const [order, setOrder] = useState(initial.order || 0);
  const [active, setActive] = useState(initial.active !== false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true); setErr("");
    const result = await onSubmit({
      product, anchor: anchor.trim().toLowerCase(),
      title: title.trim(), author: author.trim(), why: why.trim(),
      order: Number(order) || 0, active,
    });
    if (result) setErr(result);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-book-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="flex flex-col">
          <span className="k-label mb-1">product</span>
          <select className="k-input" value={product} onChange={(e) => setProduct(e.target.value)}
                  data-testid="admin-book-form-product">
            <option value="start">Start</option>
            <option value="grow">Grow</option>
          </select>
        </label>
        <label className="flex flex-col sm:col-span-2">
          <span className="k-label mb-1">anchor</span>
          <input className="k-input" value={anchor} onChange={(e) => setAnchor(e.target.value)}
                 placeholder="investigative" data-testid="admin-book-form-anchor" required />
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col">
          <span className="k-label mb-1">title</span>
          <input className="k-input" value={title} onChange={(e) => setTitle(e.target.value)}
                 data-testid="admin-book-form-title" required />
        </label>
        <label className="flex flex-col">
          <span className="k-label mb-1">author</span>
          <input className="k-input" value={author} onChange={(e) => setAuthor(e.target.value)}
                 data-testid="admin-book-form-author" required />
        </label>
      </div>
      <label className="flex flex-col">
        <span className="k-label mb-1">why (one line)</span>
        <textarea className="k-input" rows={2} value={why}
                  onChange={(e) => setWhy(e.target.value)}
                  data-testid="admin-book-form-why" required maxLength={280} />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col">
          <span className="k-label mb-1">order (lower first)</span>
          <input type="number" className="k-input" value={order}
                 onChange={(e) => setOrder(e.target.value)} min={0}
                 data-testid="admin-book-form-order" />
        </label>
        <label className="flex items-center gap-2 mt-6">
          <input type="checkbox" checked={active}
                 onChange={(e) => setActive(e.target.checked)}
                 data-testid="admin-book-form-active" />
          <span>active</span>
        </label>
      </div>
      {err && <div className="k-quiet !text-sm" style={{ color: "hsl(0 60% 45%)" }}>{err}</div>}
      <div className="flex items-center gap-2">
        <button type="submit" className="k-btn" disabled={busy}
                data-testid="admin-book-form-submit">
          {busy ? "Saving..." : "Save"}
        </button>
        <button type="button" className="k-btn-outline" onClick={onCancel}
                data-testid="admin-book-form-cancel">
          Cancel
        </button>
      </div>
    </form>
  );
}


function SignForm({ onSubmit, onCancel }) {
  const [reviewerId, setReviewerId] = useState("");
  const [signature, setSignature] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true); setErr("");
    const result = await onSubmit({
      reviewer_id: reviewerId.trim(),
      signature: signature.trim(),
    });
    if (result) setErr(result);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="admin-book-sign-form">
      <div className="k-quiet !text-sm">
        Sign this entry. Your reviewer id and a signature phrase are recorded on the row.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex flex-col">
          <span className="k-label mb-1">reviewer id</span>
          <input className="k-input" value={reviewerId}
                 onChange={(e) => setReviewerId(e.target.value)}
                 data-testid="admin-book-sign-reviewer" required />
        </label>
        <label className="flex flex-col">
          <span className="k-label mb-1">signature</span>
          <input className="k-input" value={signature}
                 onChange={(e) => setSignature(e.target.value)}
                 data-testid="admin-book-sign-signature" required />
        </label>
      </div>
      {err && <div className="k-quiet !text-sm" style={{ color: "hsl(0 60% 45%)" }}>{err}</div>}
      <div className="flex items-center gap-2">
        <button type="submit" className="k-btn" disabled={busy}
                data-testid="admin-book-sign-submit">
          {busy ? "Signing..." : "Sign entry"}
        </button>
        <button type="button" className="k-btn-outline" onClick={onCancel}
                data-testid="admin-book-sign-cancel">
          Cancel
        </button>
      </div>
    </form>
  );
}
